import React, { useEffect, useState, useCallback } from "react";
import { Spin, Alert } from "antd";
import {
  People,
  AttachMoney,
  Storefront,
  TrendingUp,
  WhatsApp,
  ShoppingCart,
  AddBusiness,
} from "@mui/icons-material";
import customClientAxios from "../../customAxios";
import { PlatformAnalyticsOverview } from "../../utils/ApiEndpoints";
import {
  PageContainer,
  StatsGrid,
  StatCard,
  StatLabel,
  StatValue,
  StatSub,
  IconBox,
  SectionTitle,
  GRADIENTS,
  fmtRupees,
  fmtNum,
} from "../paUtils";

function OverviewTab({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get(PlatformAnalyticsOverview, {
        params: { startDate, endDate },
      });
      setData(res.data.data);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load overview data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <PageContainer>
      {error && <Alert type="error" message={error} style={{ marginBottom: 20 }} />}

      <Spin spinning={loading}>
        {data && (
          <>
            {/* Signups */}
            <SectionTitle>Signups</SectionTitle>
            <StatsGrid>
              <StatCard>
                <IconBox $bg={GRADIENTS.purple}><People /></IconBox>
                <StatLabel>New Signups</StatLabel>
                <StatValue>{fmtNum(data.signups?.inRange)}</StatValue>
              </StatCard>
              {(data.signups?.byOnboarding || []).map((s) => (
                <StatCard key={s._id}>
                  <IconBox $bg={
                    s._id === "completed" ? GRADIENTS.green :
                    s._id === "pending" ? GRADIENTS.amber : GRADIENTS.teal
                  }><People /></IconBox>
                  <StatLabel>{s._id.replace(/_/g, " ")}</StatLabel>
                  <StatValue>{fmtNum(s.count)}</StatValue>
                  <StatSub>onboarding status</StatSub>
                </StatCard>
              ))}
            </StatsGrid>

            {/* Brand Pages */}
            <SectionTitle>Brand Pages</SectionTitle>
            <StatsGrid>
              <StatCard>
                <IconBox $bg={GRADIENTS.blue}><Storefront /></IconBox>
                <StatLabel>Created</StatLabel>
                <StatValue>{fmtNum(data.brandPages?.createdInRange)}</StatValue>
              </StatCard>
              {(data.brandPages?.byKycStatus || []).map((s) => (
                <StatCard key={s._id}>
                  <IconBox $bg={s._id === "verified" ? GRADIENTS.green : GRADIENTS.amber}>
                    <Storefront />
                  </IconBox>
                  <StatLabel>KYC {s._id}</StatLabel>
                  <StatValue>{fmtNum(s.count)}</StatValue>
                </StatCard>
              ))}
            </StatsGrid>

            {/* Revenue */}
            <SectionTitle>Revenue</SectionTitle>
            <StatsGrid>
              <StatCard>
                <IconBox $bg={GRADIENTS.green}><AttachMoney /></IconBox>
                <StatLabel>GMV</StatLabel>
                <StatValue>{fmtRupees(data.revenue?.gmv)}</StatValue>
                <StatSub>{fmtNum(data.revenue?.transactionCount)} transactions</StatSub>
              </StatCard>
              <StatCard>
                <IconBox $bg={GRADIENTS.purple}><TrendingUp /></IconBox>
                <StatLabel>Platform Fee</StatLabel>
                <StatValue>{fmtRupees(data.revenue?.platformFee)}</StatValue>
                <StatSub>convenience: {fmtRupees(data.revenue?.convenienceFee)}</StatSub>
              </StatCard>
              <StatCard>
                <IconBox $bg={GRADIENTS.blue}><AttachMoney /></IconBox>
                <StatLabel>Creator Payout</StatLabel>
                <StatValue>{fmtRupees(data.revenue?.creatorPayout)}</StatValue>
                <StatSub>GST: {fmtRupees(data.revenue?.gst)}</StatSub>
              </StatCard>
              <StatCard>
                <IconBox $bg={GRADIENTS.pink}><ShoppingCart /></IconBox>
                <StatLabel>Discount</StatLabel>
                <StatValue>{fmtRupees(data.revenue?.discount)}</StatValue>
              </StatCard>
            </StatsGrid>

            {/* WhatsApp Credits */}
            <SectionTitle>WhatsApp Credits</SectionTitle>
            <StatsGrid>
              <StatCard>
                <IconBox $bg={GRADIENTS.green}><WhatsApp /></IconBox>
                <StatLabel>Billed</StatLabel>
                <StatValue>{fmtRupees(data.whatsappCredits?.totalBilled)}</StatValue>
                <StatSub>{fmtNum(data.whatsappCredits?.purchaseCount)} purchases</StatSub>
              </StatCard>
              <StatCard>
                <IconBox $bg={GRADIENTS.teal}><WhatsApp /></IconBox>
                <StatLabel>Credits Added</StatLabel>
                <StatValue>{fmtNum(data.whatsappCredits?.creditsAdded)}</StatValue>
              </StatCard>
            </StatsGrid>

            {/* Customers */}
            <SectionTitle>Customers</SectionTitle>
            <StatsGrid>
              <StatCard>
                <IconBox $bg={GRADIENTS.blue}><ShoppingCart /></IconBox>
                <StatLabel>Unique Booking Customers</StatLabel>
                <StatValue>{fmtNum(data.customers?.uniqueBookingCustomers)}</StatValue>
              </StatCard>
              <StatCard>
                <IconBox $bg={GRADIENTS.green}><TrendingUp /></IconBox>
                <StatLabel>Repeat Customers</StatLabel>
                <StatValue>{fmtNum(data.customers?.repeatCustomers)}</StatValue>
              </StatCard>
            </StatsGrid>

            {/* Offerings */}
            <SectionTitle>Offerings</SectionTitle>
            <StatsGrid style={{ maxWidth: 260 }}>
              <StatCard>
                <IconBox $bg={GRADIENTS.amber}><AddBusiness /></IconBox>
                <StatLabel>Created</StatLabel>
                <StatValue>{fmtNum(data.offerings?.createdInRange)}</StatValue>
              </StatCard>
            </StatsGrid>
          </>
        )}
      </Spin>
    </PageContainer>
  );
}

export default OverviewTab;
